import React, { useState } from "react";
import { ArrowLeft, CheckCircle, Info, DollarSign, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useNotifications } from "../../../hooks/useNotifications";
import "./NotificationsPage.css";

const notificationIcons = {
  success: CheckCircle,
  info: Info,
  delivery: CheckCircle,
  payment: DollarSign,
  alert: AlertTriangle,
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");

  const { notifications, loading, error, markAllAsRead } = useNotifications("admin");

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "all") return true;
    if (filter === "alerts") return n.type === "alert";
    if (filter === "payments") return n.type === "payment";
    if (filter === "deliveries") return ["success", "info", "delivery"].includes(n.type);
    return true;
  });

  return (
      <div className="notifications-container">
        <div className="notifications-header">
          <button onClick={() => navigate(-1)} className="notifications-back-btn">
            <ArrowLeft size={24} />
          </button>
          <div className="notifications-title-area">
            <h1>{t.notificationsTitle || "Notifications"}</h1>
            <p>{t.notificationsSubtitle || "Stay updated with your logistics operations"}</p>
          </div>
          <button onClick={markAllAsRead} className="notifications-mark-read-btn">
            {t.markAllRead || "Mark all as read"}
          </button>
        </div>

        <div className="notifications-filters">
          {["all", "alerts", "payments", "deliveries"].map((filterType) => (
              <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`notification-filter-btn ${filter === filterType ? "active" : "inactive"}`}
              >
                {t[filterType] || filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </button>
          ))}
        </div>

        <div className="notifications-list-container">
          {loading && (
              <>
                {[1, 2, 3].map((i) => (
                    <div key={i} className="notification-card" style={{ opacity: 0.4, minHeight: 80 }} />
                ))}
              </>
          )}

          {!loading && error && (
              <div style={{ textAlign: "center", color: "#708094", padding: "40px 0" }}>
                Could not load notifications ({error}). Check that the backend is running and that your session token is valid.
              </div>
          )}

          {!loading && !error && filteredNotifications.length === 0 && (
              <div style={{ textAlign: "center", color: "#708094", padding: "40px 0" }}>
                No notifications
              </div>
          )}

          {!loading &&
              filteredNotifications.map((notification) => {
                const Icon = notificationIcons[notification.type] || Info;
                return (
                    <div
                        key={notification.id}
                        className={`notification-card ${!notification.is_read ? "unread" : ""} type-${notification.type}`}
                    >
                      <div className={`notification-icon-container type-${notification.type}`}>
                        <Icon size={24} />
                      </div>
                      <div className="notification-content">
                        <div className="notification-content-header">
                          <h3>{notification.title}</h3>
                          {!notification.is_read && <span className="notification-unread-dot" />}
                        </div>
                        <p className="notification-description">{notification.description}</p>
                        <p className="notification-time">{notification.time}</p>
                      </div>
                    </div>
                );
              })}
        </div>
      </div>
  );
}
